import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { cleanGeoQuery } from "@/lib/dashboardHelpers";

/**
 * Debounced Nominatim-backed search proxied through the backend at
 * `/api/geocode` (Nominatim rejects direct browser calls with HTTP 403
 * because a browser cannot set the identifying User-Agent header the
 * public server requires — the backend handles that plus the 1 rps
 * fair-use throttle and an in-memory LRU cache).
 *
 * Search strategy per user directive:
 *   1. First try the raw query the user typed (verbatim). Nominatim is
 *      good at parsing full addresses like "Via Roma 10, Fabriano" —
 *      stripping the street-type words used to hurt more than help.
 *   2. If that returns zero results, retry once with the sanitized
 *      version (`cleanGeoQuery` strips "frazione / via / piazza / …"
 *      prefix noise) so ambiguous inputs like "Frazione Cancelli" can
 *      still resolve.
 *
 * @param {string} query    Free-text query typed by the user.
 * @param {boolean} enabled Only fire the request when this is true
 *                          (e.g. when the panel that owns it is open).
 * @param {{lat:number, lng:number}} centre  Viewbox centre (soft hint).
 * @param {number} minChars Skip the fetch until the query is at least this long.
 * @returns {{ results: Array, searching: boolean, error: boolean }}
 *          `error` is true only when the backend request itself
 *          failed (network / 5xx). An empty `results` array with
 *          `error=false` means "search succeeded, 0 matches".
 */
export function useGeocodeSearch(query, enabled, centre, minChars = 2) {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled || !query || query.length < minChars) {
      setResults([]);
      setError(false);
      return;
    }
    setSearching(true);
    setError(false);
    const timer = setTimeout(async () => {
      const doQuery = async (q) => {
        const { data } = await api.get("/geocode", {
          params: { q, lat: centre.lat, lng: centre.lng, limit: 8 },
        });
        return Array.isArray(data) ? data : [];
      };
      try {
        // 1st attempt: verbatim user input.
        let hits = await doQuery(query);
        // 2nd attempt: strip street-type noise. Only fires when the
        // verbatim query returned nothing AND stripping actually
        // changes the string (otherwise it's the same request twice).
        if (hits.length === 0) {
          const stripped = cleanGeoQuery(query);
          if (stripped && stripped !== query) {
            hits = await doQuery(stripped);
          }
        }
        setResults(hits);
        setError(false);
      } catch {
        // Backend proxy failed (503 upstream, network drop, timeout).
        // Surface via `error` so the panel can show a service message
        // instead of the misleading "Nessun risultato".
        setResults([]);
        setError(true);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query, enabled, centre.lat, centre.lng, minChars]);

  return { results, searching, error };
}
